/**
 * @file agendaCheckView.ts
 * @brief The meeting's `agenda_check` (B49) shaped for the Agenda tab.
 *
 * @details
 * Pure, so the rules are tested without React: the order is the server's (the
 * order the person wrote the agenda), a viewer's list is ticked items only
 * and carries no AI verdict, and an unknown `ai.status` reads as no verdict
 * rather than a guessed one.
 *
 * @references docs/product/agenda-checks-b49.md, MeetingDetailView.tsx (AgendaCheckTab)
 *
 * @author DES
 * @date 02-11-2026
 */

import type { MeetingDetail } from "../schemas";

export type AgendaCheckStatus = "covered_decided" | "discussed_open" | "not_raised";

export type AgendaCheckRow = {
  noteId: string | null;
  noteTitle: string | null;
  text: string;
  ticked: boolean;
  tickedAtSecs: number | null;
  /** "12:34", from the server. */
  tickedAt: string | null;
  status: AgendaCheckStatus | null;
  aiTimeInSecs: number | null;
  answer: string | null;
  match: string | null;
  notInRecorder: boolean;
};

const STATUSES = new Set<string>(["covered_decided", "discussed_open", "not_raised"]);

/** "12:34" / "1:02:03" from seconds, when the server sent no label. */
export function tickClock(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${String(m).padStart(2, "0")}:${ss}`;
}

export function shapeAgendaCheck(check: MeetingDetail["agenda_check"]): {
  ownerView: boolean;
  total: number;
  tickedCount: number;
  items: AgendaCheckRow[];
} {
  const ownerView = check?.ownerView === true;
  const items: AgendaCheckRow[] = (check?.items ?? [])
    .map((it) => {
      const ticked = it?.ticked === true;
      const at = typeof it?.tickedAtSecs === "number" && Number.isFinite(it.tickedAtSecs) ? it.tickedAtSecs : null;
      const status = ownerView && it?.ai?.status && STATUSES.has(it.ai.status) ? it.ai.status : null;
      return {
        noteId: it?.noteId ?? null,
        noteTitle: it?.noteTitle ?? null,
        text: String(it?.text ?? "").trim(),
        ticked,
        tickedAtSecs: ticked ? at : null,
        tickedAt: ticked ? (it?.tickedAt?.trim() || (at != null ? tickClock(at) : null)) : null,
        status,
        aiTimeInSecs:
          status && typeof it?.ai?.timeInSecs === "number" ? it.ai.timeInSecs : null,
        answer: status && it?.ai?.answer?.trim() ? it.ai.answer.trim() : null,
        match: status ? (it?.match ?? null) : null,
        notInRecorder: it?.notInRecorder === true,
      };
    })
    .filter((r) => !!r.text)
    // A viewer is sent ticked items only; never show an unticked one to them.
    .filter((r) => ownerView || r.ticked);
  const tickedCount = items.filter((r) => r.ticked).length;
  const total = ownerView ? items.filter((r) => !r.notInRecorder).length : tickedCount;
  return { ownerView, total, tickedCount, items };
}
